import { useMemo } from 'react'
import moment from 'moment/moment'

import { Table, Text } from '@/ui'
import { PayrollProfile } from '../payrollProfile.slice'
import { TextColors, TypographyStyles } from '@/styles'
import { DATE_FORMATS } from '@/utils/dateHelpers'

const NET_NEW_FORECAST = 10
const MIGRATED_FORECAST = 20

const getCellColor = ({
  month,
  expectedCount,
  actualCount,
}: {
  month: string
  actualCount: number
  expectedCount: number
}) => {
  const isCurrentMonth = moment().format(DATE_FORMATS.MONTH_ABBREV) === month
  const isUpcomingMonth = moment(month, DATE_FORMATS.MONTH_ABBREV).isAfter(
    moment().endOf('month')
  )

  if (isUpcomingMonth) {
    return TextColors.WHITE
  }

  if (!isCurrentMonth) {
    return actualCount < expectedCount ? TextColors.RED : TextColors.GREEN
  }

  return actualCount < expectedCount ? TextColors.ORANGE : TextColors.GREEN
}

export const PayrollForecast = ({
  payrollProfiles,
}: {
  payrollProfiles: PayrollProfile[]
}) => {
  const monthMap = useMemo(
    () =>
      moment.monthsShort().map((month) => {
        const formattedMonth = `${month}-${moment().year()}`
        const filteredProfiles = payrollProfiles.filter(
          (profile) =>
            moment
              .utc(profile.onboardedAt)
              .format(DATE_FORMATS.MONTH_YEAR_DASH) === formattedMonth
        )
        return {
          month,
          migrated: filteredProfiles.filter(
            (profile) => profile.migratedFromGusto
          ).length,
          netNew: filteredProfiles.filter(
            (profile) => !profile.migratedFromGusto
          ).length,
        }
      }),
    [payrollProfiles]
  )

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell />
          {moment.monthsShort().map((month) => (
            <Table.HeaderCell key={month}>{month}</Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Row>
        <Table.Cell>Migrated</Table.Cell>
        {monthMap.map((monthData) => (
          <Table.Cell
            key={`migrated-${monthData.month}`}
            style={{
              backgroundColor: getCellColor({
                month: monthData.month,
                actualCount: monthData.migrated,
                expectedCount: MIGRATED_FORECAST,
              }),
            }}
          >
            <Text variant={TypographyStyles.BODY_SM}>
              <b>Forecast:</b> {MIGRATED_FORECAST}
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              <b>Actual:</b> {monthData.migrated}
            </Text>
          </Table.Cell>
        ))}
      </Table.Row>
      <Table.Row>
        <Table.Cell>Net New Heard Payroll</Table.Cell>
        {monthMap.map((monthData) => (
          <Table.Cell
            key={`netNew-${monthData.month}`}
            style={{
              backgroundColor: getCellColor({
                month: monthData.month,
                actualCount: monthData.netNew,
                expectedCount: NET_NEW_FORECAST,
              }),
            }}
          >
            <Text variant={TypographyStyles.BODY_SM}>
              <b>Forecast:</b> {NET_NEW_FORECAST}
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              <b>Actual:</b> {monthData.netNew}
            </Text>
          </Table.Cell>
        ))}
      </Table.Row>
      <Table.Row>
        <Table.Cell>Total Added for Month</Table.Cell>
        {monthMap.map((monthData) => (
          <Table.Cell
            key={`total-${monthData.month}`}
            style={{
              backgroundColor: getCellColor({
                month: monthData.month,
                actualCount: monthData.migrated + monthData.netNew,
                expectedCount: NET_NEW_FORECAST + MIGRATED_FORECAST,
              }),
            }}
          >
            <Text variant={TypographyStyles.BODY_SM}>
              <b>Forecast:</b> {NET_NEW_FORECAST + MIGRATED_FORECAST}
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              <b>Actual:</b> {monthData.migrated + monthData.netNew}
            </Text>
          </Table.Cell>
        ))}
      </Table.Row>
      <Table.Row>
        <Table.Cell>Total Cumulative</Table.Cell>
        {monthMap.map((monthData, index) => {
          const actualCount = monthMap
            .slice(0, index + 1)
            .reduce((prev, data) => data.netNew + data.migrated + prev, 0)
          const expectedCount =
            (NET_NEW_FORECAST + MIGRATED_FORECAST) * (index + 1)

          return (
            <Table.Cell
              key={`cumulative-${monthData.month}`}
              style={{
                backgroundColor: getCellColor({
                  month: monthData.month,
                  actualCount,
                  expectedCount,
                }),
              }}
            >
              <Text variant={TypographyStyles.BODY_SM}>
                <b>Forecast:</b> {expectedCount}
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                <b>Actual:</b> {actualCount}
              </Text>
            </Table.Cell>
          )
        })}
      </Table.Row>
    </Table>
  )
}
